import React, { useRef, useState } from "react";
import { IonIcon } from "@ionic/react";
import {
  resize,
  gitMerge,
  move,
  cube,
  reload,
  text,
  layersOutline
} from "ionicons/icons";
import { useLanguage } from "../helpers/LanguageContext";

export default function FloatingToolbar({
  containerRef,
  measureMode,
  onToggleMeasure,
  snappingEnabled,
  onToggleSnapping,
  controlMode,
  onToggleControlMode,
  transformMode,
  onToggleTransformMode,
  groupingEnabled,
  showMilling3D,
  onToggleMilling3D,
  millingStatus,
  onToggleGrouping,
  showSizeLabels = true,
  onToggleSizeLabels = () => {}
}) {
  const { t } = useLanguage();
  const [pos, setPos] = useState({ x: 30, y: 0 });
  const dragRef = useRef(null);

  const onMouseDown = (e) => {
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: pos.x,
      oy: pos.y,
      rect
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const { x, y, ox, oy, rect } = dragRef.current;
    const nx = Math.min(rect.width - 200, Math.max(0, ox + (e.clientX - x)));
    const ny = Math.min(rect.height - 60, Math.max(0, oy + (e.clientY - y)));
    setPos({ x: nx, y: ny });
  };

  const onMouseUp = () => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };

  return (
    <div
      style={{
        position: "absolute",
        top: pos.y,
        left: pos.x,
        display: "flex",
        gap: "0.4rem",
        background: "rgba(255,255,255,0.95)",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        padding: "0.4rem",
        zIndex: 1000,
        cursor: "grab",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none"
      }}
      onMouseDown={onMouseDown}
    >
      <ToolbarButton
        icon={resize}
        active={measureMode}
        label={t("toolbarMeasure")}
        onClick={onToggleMeasure}
      />
      <ToolbarButton
        icon={gitMerge}
        active={snappingEnabled}
        label={t("toolbarSnapping")}
        onClick={onToggleSnapping}
      />
      <ToolbarButton
        icon={transformMode === "move" ? move : reload}
        active={true}
        label={transformMode === "move" ? t("toolbarMove") : t("toolbarRotate")}
        onClick={onToggleTransformMode}
      />
      <ToolbarButton
        icon={text}
        active={showSizeLabels}
        label={
          showSizeLabels ? t("toolbarSizeLabelsOn") : t("toolbarSizeLabelsOff")
        }
        onClick={onToggleSizeLabels}
      />
      <ToolbarButton
        icon={layersOutline}
        active={showMilling3D}
        label={t("showMilling")}
        onClick={onToggleMilling3D}
      />
      {onToggleGrouping && (
        <ToolbarButton
          icon={cube}
          active={groupingEnabled}
          label={
            groupingEnabled ? t("toolbarGroupingOn") : t("toolbarGroupingOff")
          }
          onClick={onToggleGrouping}
        />
      )}
    </div>
  );
}

function ToolbarButton({ icon, label, active, onClick }) {
  const [hover, setHover] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        position: "relative",
        padding: "0.2rem 0.4rem",
        borderRadius: 6,
        cursor: "pointer",
        background: active
          ? "#0d6efd"
          : hover
            ? "rgba(13,110,253,0.08)"
            : "transparent",
        color: active ? "#fff" : hover ? "#0d6efd" : "#333",
        transform: hover ? "scale(1.08)" : "scale(1)",
        transition: "all 0.15s ease",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none"
      }}
    >
      <IonIcon icon={icon} />
      {hover && (
        <div
          style={{
            position: "absolute",
            top: "120%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#333",
            color: "#fff",
            padding: "0.25rem 0.5rem",
            borderRadius: 4,
            fontSize: "0.7rem",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 1010,
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none"
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}
